import { useMemo, useCallback } from 'react';
import { classifyAllNestedCampaigns, hasNestIntent } from '../utils/orphanDetection';

/**
 * Helper to check if an item is a template
 */
const isTemplateItem = (item) => item.isTemplate === true || item.type === 'template';

/**
 * Sort an array of items by a given order array of IDs.
 * Items in the order array come first (in that order),
 * followed by items not in the order array (in their original order).
 */
function sortByOrder(items, orderArray) {
  if (!orderArray || orderArray.length === 0) return items;
  
  const orderMap = new Map(orderArray.map((id, index) => [id, index]));
  
  return [...items].sort((a, b) => {
    const aIndex = orderMap.has(a.id) ? orderMap.get(a.id) : Infinity;
    const bIndex = orderMap.has(b.id) ? orderMap.get(b.id) : Infinity;
    
    // If both are in order array, sort by their position
    if (aIndex !== Infinity && bIndex !== Infinity) {
      return aIndex - bIndex;
    }
    
    // If only one is in order array, it comes first
    if (aIndex !== Infinity) return -1;
    if (bIndex !== Infinity) return 1;
    
    // If neither is in order array, maintain original order (by createdAt if available)
    if (a.createdAt && b.createdAt) {
      return new Date(b.createdAt) - new Date(a.createdAt); // Newest first
    }
    return 0;
  });
}

/**
 * Hook for filtering and organizing sidebar data
 * Separates sprints into top-level, nested, and trashed categories
 * 
 * UNIFIED DATA MODEL:
 * - All items (campaigns AND templates) are in the same `sprints` array
 * - Templates are distinguished by `isTemplate: true` flag
 * - Nested campaigns can exist inside BOTH campaigns AND templates
 * - The same logic handles both - no code duplication needed
 * 
 * @param {Array} sprints - All items (campaigns + templates in unified array)
 * @param {Array} sprintOrder - Optional array of sprint IDs defining the order
 * @param {object} options
 * @param {boolean} options.deferOrphanDetection - Defer orphan surfacing while data is still loading
 */
export function useSidebarData(sprints, sprintOrder = [], { deferOrphanDetection = false, trashedDocs = [] } = {}) {
  const {
    topLevelSprints, 
    topLevelTemplates,
    nestedSprints, 
    nestedInTemplates, 
    trashedSprints, 
    trashedTemplates, 
    orphanedNestedSprints,
    orphanedDetails,
    isNestedDataLoading,
  } = useMemo(() => {
    // Top-level campaigns: NO nest intent at all (never nested, never referenced).
    // Campaigns whose parent is missing arrive at root via the `orphaned` list only,
    // so we must not also include them here (that caused duplicate React keys).
    const topLevelCampaigns = sprints.filter((s) =>
      !hasNestIntent(s, sprints) &&
      !s.deletedAt &&
      s.status === 'active' &&
      !isTemplateItem(s)
    );
    
    // Top-level templates: isTemplate flag, not nested, not deleted
    const topLevelTempl = sprints.filter(s => 
      isTemplateItem(s) && !s.parentSprintId && !s.deletedAt
    );
    
    // Trashed campaigns (not templates).
    // Items trashed as part of an ancestor's subtree (_trashedViaAncestorId) are not
    // listed individually — the bin shows only the explicitly-trashed root, and
    // restore/permanent-delete of that root cascades to them.
    const trashedCampaigns = sprints.filter(
      s => s.deletedAt && !s._trashedViaAncestorId && !isTemplateItem(s)
    );
    
    // Trashed templates
    const trashedTempl = sprints.filter(
      s => s.deletedAt && !s._trashedViaAncestorId && isTemplateItem(s)
    );
    
    const {
      validNestedInCampaigns,
      validNestedInTemplates,
      orphaned,
      orphanedDetails: detectedOrphanDetails,
      isNestedDataLoading: nestedDataLoading,
    } = classifyAllNestedCampaigns(sprints, { deferOrphanDetection });
    
    // IMPORTANT: Include orphaned campaigns in top-level list so they're always visible
    let topLevelWithOrphans = [...topLevelCampaigns, ...orphaned];
    
    // Sort by sprintOrder if provided
    if (sprintOrder && sprintOrder.length > 0) {
      topLevelWithOrphans = sortByOrder(topLevelWithOrphans, sprintOrder);
    }
    
    return { 
      topLevelSprints: topLevelWithOrphans, 
      topLevelTemplates: topLevelTempl,
      nestedSprints: validNestedInCampaigns, 
      nestedInTemplates: validNestedInTemplates,
      trashedSprints: trashedCampaigns,
      trashedTemplates: trashedTempl,
      orphanedNestedSprints: orphaned,
      orphanedDetails: detectedOrphanDetails,
      isNestedDataLoading: nestedDataLoading,
    };
  }, [sprints, sprintOrder, deferOrphanDetection]);

  // All campaigns (top-level + nested) for search - excludes templates
  const allActiveCampaigns = useMemo(() => {
    return sprints.filter(s => !s.deletedAt && s.status === 'active' && !isTemplateItem(s));
  }, [sprints]);

  // Check if trashed item's parent still exists
  const getTrashedItemInfo = useCallback((sprint) => {
    const originalParentId = sprint._originalParentSprintId || sprint.parentSprintId;
    const parentExists = originalParentId
      ? sprints.some(s => s.id === originalParentId && !s.deletedAt)
      : true; // Top-level items always have "parent exists"
    return {
      ...sprint,
      isTemplate: isTemplateItem(sprint),
      isOrphaned: originalParentId && !parentExists,
      originalParentName: originalParentId
        ? sprints.find(s => s.id === originalParentId)?.name
        : null,
    };
  }, [sprints]);

  const activeSprintCount = topLevelSprints.length;
  const templateCount = topLevelTemplates.length;
  // Unified trash count includes campaigns, templates, and docs
  const trashCount = trashedSprints.length + trashedTemplates.length + trashedDocs.length;
  const orphanedCount = orphanedNestedSprints.length;

  // Combine trashed items for unified trash display
  const allTrashedItems = useMemo(() => {
    const campaignItems = trashedSprints.map(s => ({ ...s, itemType: 'campaign' }));
    const templateItems = trashedTemplates.map(t => ({ ...t, itemType: 'template' }));
    const docItems = trashedDocs.map((d) => ({
      id: d.id,
      name: d.title || 'Untitled Doc',
      title: d.title || 'Untitled Doc',
      icon: d.icon,
      tags: d.tags || [],
      deletedAt: d.deletedAt,
      expiresAt: d.expiresAt,
      itemType: 'doc',
    }));
    return [...campaignItems, ...templateItems, ...docItems].sort((a, b) =>
      new Date(b.deletedAt) - new Date(a.deletedAt)
    );
  }, [trashedSprints, trashedTemplates, trashedDocs]);

  return {
    topLevelSprints,
    topLevelTemplates,
    nestedSprints,
    nestedInTemplates,
    trashedSprints,
    trashedTemplates,
    allTrashedItems,
    orphanedNestedSprints,
    orphanedDetails,
    isNestedDataLoading,
    allActiveCampaigns,
    getTrashedItemInfo,
    activeSprintCount,
    templateCount,
    trashCount,
    orphanedCount,
  };
}

export default useSidebarData;

